import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminMileagesApi } from "../../http/mileages/api";
import { MileageNoCarDependency } from "../../http/mileages/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import {
  convertKilometersToMiles,
  formatDateNumeric,
  formatNumber,
  formatTimeStampNumeric,
  isWithinLast48h,
} from "../../types/utils";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import DateColumnFilter from "../ui/columnFilters/DateColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CreateMileageDialog from "./CreateMileageDialog";
import DeleteMileageDialog from "./DeleteMileageDialog";
import EditMileageDialog from "./EditMileageDialog";
import MileageHistoryDialog from "./MileageHistoryDialog";
import SendMileageRemindersDialog from "./SendMileageRemindersDialog";

const MILEAGE_FIELDS = [
  "id",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "date",
  "distance",
  "versionNumber",
  "createdAt",
  "author.id",
  "author.fullName",
  "author.email",
] as const;
type MileageField = (typeof MILEAGE_FIELDS)[number];
type MileageSearch = NestedPick<MileageNoCarDependency, MileageField>;

type MileagesProps = {
  onUpdate?: () => void;
};

const Mileages: FC<MileagesProps> = ({ onUpdate }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const [showNewItem, setShowNewItem] = useState(false);
  const [showSendReminders, setShowSendReminders] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MileageSearch>();
  const [itemToEdit, setItemToEdit] = useState<MileageSearch>();

  const mileagesApi = useAdminMileagesApi();
  const [itemToShowVersions, setItemToShowVersions] = useState<MileageSearch>();
  const { id: carId } = useParams<{ id: string }>();

  const query: PaginationQueryWithFields<MileageSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: MILEAGE_FIELDS,
  };

  const result = mileagesApi.useCarMileages(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: mileagesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: MileageSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.mileages.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item), false));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.mileages.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<MileageSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.mileages.insert && (
            <Button buttonProps={{ onClick: () => setShowNewItem(true) }} glyph={SvgAdd} />
          ),
          secondary: !carId &&
            userSettings.permissions.mileages.insert &&
            userSettings.permissions.subsidiaries.read && (
              <Button buttonProps={{ onClick: () => setShowSendReminders(true) }}>
                {i18n.t.mileages.sendReminders}
              </Button>
            ),
        }}
        head={
          <>
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="mileage.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="mileage.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            {/* <Table.Th title={i18n.t.common.date} fieldName="mileage.date" order={orderOptions} /> */}
            <Table.Th
              title={i18n.t.common.date}
              fieldName="mileage.date"
              order={orderOptions}
              filters={filterOptions}
              filter={DateColumnFilter}
              formatter={(value) =>
                typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
              }
            />
            <Table.Th title={i18n.t.common.mileage} fieldName="mileage.distance" order={orderOptions} />
            {!!carId && (
              <>
                <Table.Th title={i18n.t.common.createdAt} fieldName="mileage.createdAt" order={orderOptions} />
                <Table.Th
                  title={i18n.t.common.createdBy}
                  fieldName="mileage.createdBy"
                  order={orderOptions}
                  filters={filterOptions}
                  filter={TextColumnFilter}
                />
              </>
            )}
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td align="left">
                  {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td align="left">
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td align="left">
              {!!carId && isWithinLast48h(item.createdAt) && <NewItemIcon />}
              {formatDateNumeric(item.date, i18n.language)}
            </td>
            <Table.Td noWrap>
              {formatNumber(
                !!userSettings.me.distanceInMiles ? convertKilometersToMiles(item.distance) : item.distance,
                i18n.language,
                {
                  showThousendSeparator: true,
                  fractionalDigits: 0,
                },
              ) + (!!userSettings.me.distanceInMiles ? ` ${i18n.t.common.miles}` : " km")}
            </Table.Td>
            {!!carId && (
              <>
                <td align="left">{formatTimeStampNumeric(item.createdAt, i18n.language)}</td>
                <td align="left">{item.author.fullName}</td>
              </>
            )}
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {itemToDelete && (
        <DeleteMileageDialog
          mileageId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!itemToEdit && (
        <EditMileageDialog
          mileageId={itemToEdit.id}
          onClose={(success) => {
            setItemToEdit(undefined);
            success && onUpdate?.();
          }}
          carId={carId}
          allowEdit
        />
      )}

      {!!showNewItem && (
        <CreateMileageDialog
          onClose={(success) => {
            setShowNewItem(false);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!showSendReminders && <SendMileageRemindersDialog onClose={() => setShowSendReminders(false)} />}

      {!!itemToShowVersions && (
        <MileageHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.mileages.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default Mileages;
